import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../main/Apicall';
import Pagination from '../Reusablecomponents/Pagination';
import ToastPopup from '../Popups/ToastPopup';
import DeleteConfirmationModal from '../Popups/DeleteConfirmationModal';
import '../css/main/User.css';
import { Users, createToastMessage, ToastMessage } from "../Interfaces/Interface"
const User: React.FC = () => {
    const [users, setUsers] = useState<Users[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [totalElements, setTotalElements] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [message, setMessage] = useState<ToastMessage | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '' });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState<string | null>(null);
    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await apiCall('get', `/api/users/all`);
            setUsers(data || []);
            setTotalElements(data?.length || 0);
            setTotalPages(Math.ceil((data?.length || 0) / itemsPerPage));
        } catch (err: any) {
            setMessage(createToastMessage("Failed to load users.", "error", 3000));
        } finally {
            setIsLoading(false);
        }
    }, [itemsPerPage]);
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers, currentPage]);
    const handleCreateNew = () => {
        setFormData({ email: '', password: '', confirmPassword: '' });
        setShowForm(true);
    };
    const handleDeleteClick = (userId: string) => {
        setUserToDelete(userId);
        setShowDeleteModal(true);
    };
    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;
        try {
            await apiCall('delete', `api/users/${userToDelete}`);
            setMessage(createToastMessage("User deleted successfully.", "success", 3000));
            fetchUsers();
        } catch (err: any) {
            setMessage(createToastMessage("Failed to delete user.", "error", 3000));
        } finally {
            setShowDeleteModal(false);
            setUserToDelete(null);
        }
    };
    const validateForm = () => {
        if (!formData.email.trim()) {
            setMessage(createToastMessage('Email is required', "error", 3000));
            return false;
        }
        if (!formData.email.includes('@')) {
            setMessage(createToastMessage('Please enter a valid email', "error", 3000));
            return false;
        }
        if (!formData.password) {
            setMessage(createToastMessage('Password is required', "error", 3000));
            return false;
        }
        if (formData.password.length < 6) {
            setMessage(createToastMessage('Password must be at least 6 characters', "error", 3000));
            return false;
        }
        if (formData.password !== formData.confirmPassword) {
            setMessage(createToastMessage('Passwords do not match', "error", 3000));
            return false;
        }
        return true;
    };
    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            setIsLoading(true);
            const userData = { email: formData.email, password: formData.password };
            await apiCall('post', 'api/users', userData);
            setMessage(createToastMessage('User created successfully', "success", 3000));
            setShowForm(false);
            fetchUsers();
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Failed to create user';
            setMessage(createToastMessage((errorMsg), "error", 3000));
        } finally {
            setIsLoading(false);
        }
    };
    const getPaginatedUsers = () => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return users.slice(startIndex, endIndex);
    };
    return (
        <div className="table-details">
            <div className="table-title">
                <h2>Users Management</h2>
                <div className="table-title-actions">
                    <button className="createnewuser" onClick={handleCreateNew}> Create New User </button>
                </div>
            </div>
            {showForm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Create New User</h3>
                            <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Email:</label>
                                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Enter email address" className="form-input" />
                            </div>
                            <div className="form-group">
                                <label>Password:</label>
                                <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Enter password" className="form-input" />
                            </div>
                            <div className="form-group">
                                <label>Confirm Password:</label>
                                <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} placeholder="Confirm password" className="form-input" />
                            </div>
                        </div>
                        <div className="buttonss">
                            <button className="cancelbutton" onClick={() => setShowForm(false)}>Cancel</button>
                            <button className="savebutton" onClick={handleSubmit} disabled={isLoading}>{isLoading ? 'Saving...' : 'Create'}</button>
                        </div>
                    </div>
                </div>
            )}
            <div className="table-container">
                <div className="table-inner-container">
                    <table className="main-table">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Email</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={3} className="loading-cell"> Loading users...</td>
                                </tr>
                            ) : getPaginatedUsers().length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="no-data"> No users found. Click "Create New User" to add one.</td>
                                </tr>
                            ) : (
                                getPaginatedUsers().map((user, index) => (
                                    <tr key={user.id}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{user.email}</td>
                                        <td>
                                            <button className="action-icon delete" onClick={() => handleDeleteClick(user.id)} title="Delete"> Delete</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalElements={totalElements}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
            />
            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => { setShowDeleteModal(false); setUserToDelete(null) }}
                onConfirm={handleDeleteConfirm}
            />
            {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
        </div>
    );
};
export default User;